import React, { useState, useEffect } from 'react';
import { Zap, Plus, Trash2, Send, CheckCircle, XCircle, ExternalLink, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = localStorage.getItem('adminToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const EVENT_LABELS = {
  'blog.published': 'New blog post published',
  'thumbnail.generated': 'New blog thumbnail generated',
};

const SocialWebhooks = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [supportedEvents, setSupportedEvents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ url: '', label: '', events: ['blog.published'] });
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [wRes, lRes] = await Promise.all([
        fetch(`${API_URL}/api/social-webhooks`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/social-webhooks/logs`, { headers: authHeaders() }),
      ]);
      const wData = await wRes.json();
      const lData = await lRes.json();
      setWebhooks(wData.webhooks || []);
      setSupportedEvents(wData.supported_events || []);
      setLogs(lData.logs || []);
    } catch (e) {
      console.error('Failed to load webhooks', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const toggleEvent = (ev) => {
    setForm((f) => ({
      ...f,
      events: f.events.includes(ev) ? f.events.filter((e) => e !== ev) : [...f.events, ev],
    }));
  };

  const handleCreate = async () => {
    if (!form.url.trim().startsWith('http')) {
      alert('Please paste a valid webhook URL (must start with https://)');
      return;
    }
    if (form.events.length === 0) {
      alert('Pick at least one event to trigger this webhook.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/social-webhooks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setForm({ url: '', label: '', events: ['blog.published'] });
        fetchAll();
      } else {
        const err = await res.json();
        alert('Failed: ' + (err.detail || 'unknown error'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this webhook?')) return;
    await fetch(`${API_URL}/api/social-webhooks/${id}`, { method: 'DELETE', headers: authHeaders() });
    fetchAll();
  };

  const handleToggleActive = async (hook) => {
    await fetch(`${API_URL}/api/social-webhooks/${hook.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ active: !hook.active }),
    });
    fetchAll();
  };

  const handleTest = async (id) => {
    const res = await fetch(`${API_URL}/api/social-webhooks/${id}/test`, { method: 'POST', headers: authHeaders() });
    const data = await res.json();
    alert(data.ok
      ? `✅ Test delivered (HTTP ${data.status}). Check your Zap/Scenario history.`
      : `❌ Test failed: ${data.error || 'HTTP ' + data.status}`);
    fetchAll();
  };

  return (
    <div className="p-6 max-w-5xl" data-testid="social-webhooks-page">
      <div className="flex items-center gap-3 mb-2">
        <Zap className="w-7 h-7 text-teal-600" />
        <h1 className="text-2xl font-bold text-slate-800">Social Auto-Post (Zapier / Make)</h1>
      </div>
      <p className="text-slate-600 mb-6 text-sm max-w-2xl">
        Automatically push new blog posts to Facebook, Pinterest, X, and more. Create a
        Zap (or Make scenario) with a <strong>"Catch Hook"</strong> trigger, paste its URL below, and
        Fort Worth Dental will POST every new post to it.
      </p>

      {/* How-to */}
      <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-6 text-sm text-teal-900">
        <p className="font-semibold mb-1">Quick setup (2 minutes):</p>
        <ol className="list-decimal list-inside space-y-1">
          <li>In Zapier, create a Zap → trigger <strong>"Webhooks by Zapier → Catch Hook"</strong> (Make: <strong>"Custom Webhook"</strong>).</li>
          <li>Copy the generated URL and paste it below.</li>
          <li>Add an action: <strong>Facebook Pages / Pinterest / X → Create Post</strong>, mapping the <code>title</code>, <code>url</code>, <code>summary</code> and <code>image</code> fields we send.</li>
          <li>Click <strong>Test</strong> here to fire a sample payload and confirm it arrives.</li>
        </ol>
        <a href="https://zapier.com/apps/webhook/integrations" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-teal-700 font-semibold hover:underline">
          Zapier Webhooks docs <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Create form */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-8">
        <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Plus className="w-4 h-4" /> Add a webhook</h2>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Webhook URL</label>
            <input
              data-testid="webhook-url-input"
              type="url"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://hooks.zapier.com/hooks/catch/..."
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Label (optional)</label>
            <input
              data-testid="webhook-label-input"
              type="text"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. Facebook + Pinterest Zap"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none"
            />
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-xs font-medium text-slate-500 mb-2">Fire on</label>
          <div className="flex flex-wrap gap-3">
            {(supportedEvents.length ? supportedEvents : Object.keys(EVENT_LABELS)).map((ev) => (
              <label key={ev} className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  data-testid={`webhook-event-${ev}`}
                  type="checkbox"
                  checked={form.events.includes(ev)}
                  onChange={() => toggleEvent(ev)}
                  className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                {EVENT_LABELS[ev] || ev}
              </label>
            ))}
          </div>
        </div>
        <button
          data-testid="webhook-save-btn"
          onClick={handleCreate}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-5 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
        >
          <Plus className="w-4 h-4" /> {saving ? 'Saving…' : 'Add Webhook'}
        </button>
      </div>

      {/* List */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-slate-800">Your webhooks</h2>
        <button onClick={fetchAll} className="text-slate-500 hover:text-slate-700" data-testid="webhooks-refresh-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      {webhooks.length === 0 ? (
        <p className="text-slate-400 text-sm mb-8" data-testid="webhooks-empty">No webhooks yet. Add one above to start auto-posting.</p>
      ) : (
        <div className="space-y-3 mb-8" data-testid="webhooks-list">
          {webhooks.map((h) => (
            <div key={h.id} className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${h.active ? 'bg-green-500' : 'bg-slate-300'}`} />
                  <span className="font-semibold text-slate-800">{h.label || 'Webhook'}</span>
                </div>
                <p className="text-xs text-slate-500 truncate mt-1">{h.url}</p>
                <p className="text-xs text-slate-400 mt-1">{(h.events || []).map((e) => EVENT_LABELS[e] || e).join(', ')}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => handleTest(h.id)} data-testid={`webhook-test-${h.id}`} className="inline-flex items-center gap-1 text-xs bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg text-slate-700">
                  <Send className="w-3 h-3" /> Test
                </button>
                <button onClick={() => handleToggleActive(h)} className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700">
                  {h.active ? 'Disable' : 'Enable'}
                </button>
                <button onClick={() => handleDelete(h.id)} data-testid={`webhook-delete-${h.id}`} className="text-red-500 hover:text-red-600 p-1.5">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delivery logs */}
      {logs.length > 0 && (
        <div>
          <h2 className="font-semibold text-slate-800 mb-3">Recent deliveries</h2>
          <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 text-sm" data-testid="webhook-logs">
            {logs.map((l) => (
              <div key={l.id} className="flex items-center gap-3 px-4 py-2">
                {l.ok ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                <span className="text-slate-700 flex-1">{l.label || 'Webhook'} · {l.event}</span>
                <span className="text-slate-400 text-xs">{l.status || l.error || ''}</span>
                <span className="text-slate-400 text-xs">{(l.delivered_at || '').slice(0, 16).replace('T', ' ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialWebhooks;
